/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCamera, faVideo, faPlay, faImages, faXmark,
  faChevronLeft, faChevronRight  } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from 'react-i18next';
import emptyBanner from '../../../images/empty_banner.jpeg';

import UpdateActorHeadshotModal from "../../UpdateActorHeadshotModal";
import UpdateBannerModal from "../../UpdateBannerModal"; 
import VideoPlayer from "../../common/VideoPlayer";
import PlayButtonK from "../../common/PlayButtonK";
import { isVideoKey } from '../../../utils/mediaTypes';

export default function UserHero({ data, scrollToPhotos, scrollToVideos, isEditMode, isOwner, onEnterEdit, onChange, errors = {}, clearError }) {
  const { t } = useTranslation();
  const [isHeadshotModalOpen, setIsHeadshotModalOpen] = useState(false);
  const [isBannerModalOpen, setIsBannerModalOpen] = useState(false);
  const [playingView, setPlayingView] = useState(null);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const headshots = useMemo(() => data?.photo_albums?.headshots || [], [data?.photo_albums?.headshots]);
  const hasMultiple = headshots.length > 1;

  const [localHeadshot, setLocalHeadshot] = useState(null);
  const [localBanner, setLocalBanner] = useState(null);
  const [localReel, setLocalReel] = useState(null);

  const AWS_URL = process.env.REACT_APP_AWS_URL;

  useEffect(() => {
    if (!isEditMode) {
      setLocalHeadshot(null);
      setLocalBanner(null);
      setLocalReel(null);
      setPlayingView(null);
    }
  }, [isEditMode]);

  useEffect(() => {
    if (headshots.length > 0) {
      const mainIndex = headshots.findIndex(img => img.isMain);
      if (mainIndex !== -1) {
        setCurrentIndex(mainIndex);
      }
    }
  }, [headshots]);

  const nextImage = (e) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % headshots.length);
  };
  
  const prevImage = (e) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + headshots.length) % headshots.length);
  };
  
  const handleTouchStart = (e) => setTouchStart(e.targetTouches[0].clientX);
  const handleTouchEnd = (e) => {
    const touchEnd = e.changedTouches[0].clientX;
    if (touchStart - touchEnd > 50) nextImage();
    if (touchEnd - touchStart > 50) prevImage();
  };
  
  const getImageUrl = (imgObj) => {
    if (!imgObj) return emptyBanner;
    const path = typeof imgObj === 'string' ? imgObj : imgObj.image;
    return path.startsWith("http") ? path : `${AWS_URL}/${path}`;
  };

  const mainHeadshotPath = headshots.find(img => img.isMain)?.image;
  const activeImgObj = headshots[currentIndex] || mainHeadshotPath || data?.picture;

  const mainReelObj = data?.video_gallery?.reels?.find(vid => vid.isMain);
  // Legacy profiles stored the reel video in bannerImg: play it as the reel,
  // never render it as an <img>.
  const bannerIsVideo = isVideoKey(data?.bannerImg);
  const reelUrl = mainReelObj?.url || (bannerIsVideo ? data.bannerImg : "");
  const dbReelSrc = !reelUrl ? "" : reelUrl.startsWith("http") ? reelUrl : `${AWS_URL}/${reelUrl}`;
  const reelThumb = mainReelObj?.thumbnail;
  const staticBanner = bannerIsVideo ? null : data?.bannerImg;
  const dbBannerUrl = reelThumb
    ? (reelThumb.startsWith("http") ? reelThumb : `${AWS_URL}/${reelThumb}`)
    : staticBanner
      ? (staticBanner.startsWith("http") ? staticBanner : `${AWS_URL}/${staticBanner}`)
      : emptyBanner;

  const displayHeadshot = localHeadshot || getImageUrl(activeImgObj);
  const displayBanner = localBanner || dbBannerUrl;
  const displayReel = localReel || dbReelSrc;
  const hasReel = Boolean(displayReel);

  const handleSaveHeadshot = (res) => {
    setIsHeadshotModalOpen(false);
    if (res.type === "local") {
      setLocalHeadshot(URL.createObjectURL(res.file));
      onChange("new_headshot_file", res.file); 
    } else {
      setLocalHeadshot(`${AWS_URL}/${res.data.image}`);
      onChange("add_to_gallery", { type: "headshot", url: res.data.image });
    }
  };

  const base64ToFile = async (base64String, fileName) => {
    const res = await fetch(base64String);
    const blob = await res.blob();
    return new File([blob], fileName, { type: 'image/jpeg' });
  };

  const handleSaveBanner = async (res) => {
    setIsBannerModalOpen(false);

    if (res.type === "image") {
      setLocalBanner(URL.createObjectURL(res.file));
      setLocalReel(null);
      onChange("new_banner_file", res.file);
    } else if (res.type === "video") {
      setLocalReel(URL.createObjectURL(res.file));
      let thumbFile = res.customThumbnailFile;
      if (!thumbFile && res.thumbnailData) {
        thumbFile = await base64ToFile(res.thumbnailData, "reel_thumb.jpg");
      }
      if (thumbFile) setLocalBanner(URL.createObjectURL(thumbFile));
      onChange("new_reel_file", res.file);
      onChange("new_reel_thumbnail", thumbFile);
    }
  };

  const handleOwnerUploadClick = (e) => {
    e.stopPropagation();
    onEnterEdit?.();
    setIsHeadshotModalOpen(true);
  };

  const handleSetMain = (imageKey) => {
    const newHeadshots = (data?.photo_albums?.headshots || []).map((img) => ({
      ...img,
      isMain: img.image === imageKey 
    }));

    const newAlbums = {
      ...data.photo_albums,
      headshots: newHeadshots
    };

    onChange("photo_albums", newAlbums);
    
    setIsHeadshotModalOpen(false);
  };

  return (
    <>
      <div className="tw-w-full tw-max-w-[1280px] tw-mx-auto tw-pb-10">
        
        {/* DESKTOP VIEW */}
        <div className="tw-hidden md:tw-flex tw-w-full tw-h-[515px] xl:tw-h-[600px] tw-gap-8 tw-relative">
          
          <div 
            className={`tw-relative tw-w-[343px] tw-h-full tw-shrink-0 tw-rounded-[10px] tw-overflow-hidden tw-bg-black group tw-cursor-pointer ${
              errors.image_details ? 'tw-border-[3px] tw-border-red-500' : 'tw-shadow-[0_20px_40px_rgba(0,0,0,0.6)]'
            }`}
            onClick={() => !isEditMode && setIsExpanded(true)}
          >
            <img src={displayHeadshot} alt="Headshot" className="tw-w-full tw-h-full tw-object-cover" />
            
            {hasMultiple && !isEditMode && (
              <>
                <button 
                  onClick={prevImage} className="tw-absolute tw-left-2 tw-top-1/2 tw--translate-y-1/2 tw-z-20 tw-bg-transparent tw-border-none tw-text-white tw-drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] hover:tw-text-[#FF00A0] tw-transition-all tw-cursor-pointer"
                >
                  <FontAwesomeIcon icon={faChevronLeft} className="tw-text-2xl" />
                </button>
                <button 
                  onClick={nextImage} className="tw-absolute tw-right-2 tw-top-1/2 tw--translate-y-1/2 tw-z-20 tw-bg-transparent tw-border-none tw-text-white tw-drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] hover:tw-text-[#FF00A0] tw-transition-all tw-cursor-pointer"
                >
                  <FontAwesomeIcon icon={faChevronRight} className="tw-text-2xl" />
                </button>
              </>
            )}

            {isEditMode && (
              <div className="tw-absolute tw-inset-0 tw-bg-[#1F0439]/40 tw-backdrop-blur-[1px] tw-flex tw-flex-col tw-items-center tw-justify-center">
                <button onClick={(e) => { e.stopPropagation(); setIsHeadshotModalOpen(true); }} className="tw-bg-transparent tw-border-none tw-flex tw-flex-col tw-items-center tw-gap-3 hover:tw-scale-105 tw-transition-transform tw-cursor-pointer">
                  <div className="tw-w-[59px] tw-h-[56px] tw-bg-[#371E51]/80 tw-rounded-full tw-flex tw-items-center tw-justify-center">
                    <FontAwesomeIcon icon={faCamera} className="tw-text-[#FFB0CF] tw-text-xl" />
                  </div>
                  <span className="tw-text-[#F0DBFF] tw-text-xs tw-font-bold tw-uppercase tw-tracking-widest">{t("Update Headshot")}</span>
                </button>
              </div>
            )}

            {isOwner && !isEditMode && (
              <button
                onClick={handleOwnerUploadClick}
                className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-black/10 tw-border-none tw-text-white tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity tw-cursor-pointer"
                title={t("Update Photo")}
              >
                <FontAwesomeIcon icon={faCamera} className="tw-text-2xl tw-drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)]" />
              </button>
            )}
          </div>

          <div className={`tw-relative tw-flex-1 tw-h-full tw-rounded-[10px] tw-overflow-hidden tw-bg-black ${
             errors.trailerOrBanner ? 'tw-border-[3px] tw-border-red-500' : 'tw-shadow-[0_20px_40px_rgba(0,0,0,0.6)]'
          }`}>
            {playingView === 'desktop' ? (
              <VideoPlayer src={displayReel} poster={displayBanner} autoPlay onEnded={() => setPlayingView(null)} className="tw-w-full tw-h-full" />
            ) : (
              <>
                <img src={displayBanner} alt="Banner" className="tw-w-full tw-h-full tw-object-cover" />
                <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-t tw-from-[#1E0039]/60 tw-to-transparent" />
                {hasReel && !isEditMode && (
                  <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-cursor-pointer" onClick={() => setPlayingView('desktop')}>
                    <PlayButtonK size="md" onClick={() => setPlayingView('desktop')} />
                  </div>
                )}
                {isEditMode && (
                  <div className="tw-absolute tw-inset-0 tw-bg-[#1F0439]/40 tw-backdrop-blur-[1px] tw-flex tw-items-center tw-justify-center">
                    <button onClick={(e) => { e.stopPropagation(); setIsBannerModalOpen(true); }} className="tw-bg-transparent tw-border-none tw-flex tw-flex-col tw-items-center tw-gap-4 hover:tw-scale-105 tw-transition-transform tw-cursor-pointer">
                      <div className="tw-w-[72px] tw-h-[66px] tw-bg-[#371E51]/80 tw-rounded-full tw-flex tw-items-center tw-justify-center">
                        <FontAwesomeIcon icon={faVideo} className="tw-text-[#FFB0CF] tw-text-2xl" />
                      </div>
                      <span style={{ color: '#1F0039' }} className="tw-bg-white/90 tw-text-sm tw-font-bold tw-uppercase tw-tracking-widest tw-px-4 tw-py-1.5 tw-rounded-full">{t("Update Reel / Banner")}</span>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/*  MOBILE VIEW */}
        <div className="tw-flex md:tw-hidden tw-flex-col tw-gap-4">
          
          <div className="tw-relative tw-w-full tw-h-[250px] tw-rounded-[10px] tw-overflow-hidden tw-bg-black tw-shadow-lg">
            {playingView === 'mobile' ? (
              <VideoPlayer src={displayReel} poster={displayBanner} autoPlay onEnded={() => setPlayingView(null)} className="tw-w-full tw-h-full" />
            ) : (
              <>
                <img src={displayBanner} alt="Mobile Banner" className="tw-w-full tw-h-full tw-object-cover" />
                {hasReel && !isEditMode && (
                  <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center" onClick={() => setPlayingView('mobile')}>
                    <PlayButtonK size="sm" onClick={() => setPlayingView('mobile')} />
                  </div>
                )}
                {isEditMode && (
                  <div className="tw-absolute tw-inset-0 tw-bg-black/50 tw-flex tw-items-center tw-justify-center">
                    <button onClick={() => setIsBannerModalOpen(true)} className="tw-bg-[#FF43A7] tw-border-none tw-text-[#570033] tw-px-6 tw-py-2 tw-rounded-full tw-font-bold tw-text-sm">
                      <FontAwesomeIcon icon={faVideo} className="tw-mr-2" /> {t("Update Reel")}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="tw-flex tw-flex-row tw-w-full tw-gap-4 tw-items-stretch">
            <div 
              className="tw-relative tw-w-1/2 tw-min-h-[220px] tw-rounded-[10px] tw-overflow-hidden tw-bg-black tw-shadow-lg"
              onClick={() => !isEditMode && setIsExpanded(true)}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              <img src={displayHeadshot} alt="Mobile Headshot" className="tw-w-full tw-h-full tw-object-cover" />
              
              {hasMultiple && (
                <>
                  <button 
                    onClick={prevImage} className="tw-hidden">
                    <FontAwesomeIcon icon={faChevronLeft} className="tw-text-4xl" />
                  </button>

                  <button 
                    onClick={nextImage} 
                   className="tw-hidden">
                    <FontAwesomeIcon icon={faChevronRight} className="tw-text-4xl" />
                  </button>
                </>
              )}
              {isEditMode && (
                <div className="tw-absolute tw-inset-0 tw-bg-black/50 tw-flex tw-items-center tw-justify-center">
                  <button onClick={(e) => { e.stopPropagation(); setIsHeadshotModalOpen(true); }} className="tw-bg-[#FF43A7] tw-border-none tw-text-[#570033] tw-px-4 tw-py-2 tw-rounded-lg tw-font-bold tw-text-xs">
                    {t("Edit Photo")}
                  </button>
                </div>
              )}

              {isOwner && !isEditMode && (
                <button
                  onClick={handleOwnerUploadClick}
                  className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-h-1/3 tw-flex tw-items-end tw-justify-center tw-gap-2 tw-pb-3 tw-bg-gradient-to-t tw-from-[#1F0439]/90 tw-to-transparent tw-border-none tw-text-[#F0DBFF] tw-cursor-pointer"
                >
                  <FontAwesomeIcon icon={faCamera} className="tw-text-sm" />
                  <span className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wider">{t("Update Photo")}</span>
                </button>
              )}
            </div>

            <div className="tw-w-1/2 tw-flex tw-flex-col tw-justify-center tw-gap-4">
              <button onClick={scrollToVideos} className="tw-flex tw-items-center tw-justify-center tw-gap-3 tw-w-full tw-py-4 tw-rounded-[8px] tw-bg-transparent tw-border-[1.5px] tw-border-[#FF43A7] tw-text-white tw-font-bold">
                <FontAwesomeIcon icon={faPlay} /> {t("Videos")}
              </button>
              <button onClick={scrollToPhotos} className="tw-flex tw-items-center tw-justify-center tw-gap-3 tw-w-full tw-py-4 tw-rounded-[8px] tw-bg-transparent tw-border-[1.5px] tw-border-[#FF43A7] tw-text-white tw-font-bold">
                <FontAwesomeIcon icon={faImages} /> {t("Pictures")}
              </button>
            </div>
          </div>
        </div>
      </div>

      {isExpanded && (
  <div 
    className="tw-fixed tw-inset-0 tw-z-[9999] tw-bg-[#0a0014]/95 tw-backdrop-blur-md tw-flex tw-items-center tw-justify-center tw-overflow-hidden" 
    onClick={() => setIsExpanded(false)}
    onTouchStart={handleTouchStart}
    onTouchEnd={handleTouchEnd}
  >

    {hasMultiple && (
      <>
        <button 
          onClick={prevImage} 
          className="md:tw-hidden tw-absolute tw-left-4 tw-top-1/2 tw--translate-y-1/2 tw-z-[130] 
                     tw-text-white tw-bg-transparent tw-border-none tw-drop-shadow-[0_2px_8px_rgba(0,0,0,1)]"
        >
          <FontAwesomeIcon icon={faChevronLeft} className="tw-text-4xl" />
        </button>

        <button 
          onClick={nextImage} 
          className="md:tw-hidden tw-absolute tw-right-4 tw-top-1/2 tw--translate-y-1/2 tw-z-[130] 
                     tw-text-white tw-bg-transparent tw-border-none tw-drop-shadow-[0_2px_8px_rgba(0,0,0,1)]"
        >
          <FontAwesomeIcon icon={faChevronRight} className="tw-text-4xl" />
        </button>
      </>
    )}

    <div 
      className="tw-relative tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center" 
      onClick={(e) => e.stopPropagation()} 
    >
      <div className="tw-relative">
        
        <button 
          className="tw-fixed tw-top-6 tw-right-6 md:tw-absolute md:tw-top-0 md:tw--right-24 tw-w-12 tw-h-12 tw-z-[120] tw-flex tw-items-center tw-justify-center 
           tw-bg-white/10 tw-backdrop-blur-md tw-border tw-border-white/20 tw-rounded-full tw-text-white 
           tw-drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] hover:tw-text-[#FF00A0] hover:tw-bg-white/20 
           tw-transition-all tw-cursor-pointer"
          onClick={() => setIsExpanded(false)}
        >
          <FontAwesomeIcon icon={faXmark} className="tw-text-2xl" />
        </button>

        <img 
          src={displayHeadshot} 
          alt="Fullscreen" 
          className="tw-max-w-[95vw] md:tw-max-w-[80vw] tw-max-h-[90vh] tw-object-contain tw-rounded-lg tw-shadow-2xl" 
        />

        {hasMultiple && (
          <>
            <button 
              onClick={prevImage} 
              className="tw-hidden md:tw-flex tw-absolute md:-tw-left-24 tw-top-1/2 tw--translate-y-1/2 tw-z-[100] tw-items-center tw-justify-center 
              tw-w-16 tw-h-16 tw-rounded-full tw-bg-white/10 tw-backdrop-blur-md tw-border tw-border-white/20 
              tw-text-white hover:tw-text-[#FF00A0] md:hover:tw-bg-white/20 tw-transition-all tw-cursor-pointer"
            >
              <FontAwesomeIcon icon={faChevronLeft} className="tw-text-4xl" />
            </button>

            <button 
              onClick={nextImage} 
              className="tw-hidden md:tw-flex tw-absolute md:-tw-right-24 tw-top-1/2 tw--translate-y-1/2 tw-z-[100] tw-items-center tw-justify-center 
              tw-w-16 tw-h-16 tw-rounded-full tw-bg-white/10 tw-backdrop-blur-md tw-border tw-border-white/20 
              tw-text-white hover:tw-text-[#FF00A0] md:hover:tw-bg-white/20 tw-transition-all tw-cursor-pointer"
            >
              <FontAwesomeIcon icon={faChevronRight} className="tw-text-4xl" />
            </button>
          </>
        )}
      </div>
    </div>
  </div>
)}

      <UpdateActorHeadshotModal isOpen={isHeadshotModalOpen} onClose={() => setIsHeadshotModalOpen(false)} libraryImages={data?.photo_albums?.headshots || []} mode="user" onSave={handleSaveHeadshot} onSetMain={handleSetMain}/>
      <UpdateBannerModal isOpen={isBannerModalOpen} onClose={() => setIsBannerModalOpen(false)} libraryItems={data?.video_gallery?.reels || []} onSave={handleSaveBanner} />
    </>
  );
}